<script>
  import { createEventDispatcher } from 'svelte';
  import { fade, scale } from 'svelte/transition';
  import { FontAwesomeIcon } from '@fortawesome/svelte-fontawesome';
  import { faXmark } from '@fortawesome/pro-light-svg-icons';

  export let entry;
  export let mode = 'create';
  export let onClose;
  export let onSave;

  const dispatch = createEventDispatcher();

  // Available options for type and platforms
  const typeOptions = ['String', 'Message'];
  const platformOptions = ['iOS', 'Android', 'Web', 'Messaging'];

  // Form data
  let formData = {
    _id: entry._id || null,
    key: entry.key || '',
    ruleId: entry.ruleId || null,
    type: entry.type || 'String',
    platforms: entry.platforms || [],
    placeholders: entry.placeholders || [],
    defaultLanguageCode: entry.defaultLanguageCode || 'en_US',
    defaultContent: entry.defaultContent || { value: '' }
  };

  // Validation state
  let errors = {};

  function validateForm() {
    errors = {};
    
    if (!formData.key) {
      errors.key = 'Key is required';
    }
    
    if (!formData.type) {
      errors.type = 'Type is required';
    }

    if (formData.platforms.length === 0) {
      errors.platforms = 'At least one platform is required';
    }

    if (formData.type === 'String' && !formData.defaultContent.value) {
      errors.content = 'Content value is required for String type';
    }

    if (formData.type === 'Message') {
      if (!formData.defaultContent.title) {
        errors.content = 'Title is required for Message type';
      }
      if (!formData.defaultContent.message) {
        errors.content = 'Message is required for Message type';
      }
    }

    return Object.keys(errors).length === 0;
  }

  function handleSubmit() {
    if (validateForm()) {
      onSave(formData);
    }
  }

  function handleTypeChange() {
    // Reset content when type changes
    if (formData.type === 'String') {
      formData.defaultContent = { value: '' };
    } else if (formData.type === 'Message') {
      formData.defaultContent = { title: '', message: '' };
    }
  }

  function togglePlatform(platform) {
    const index = formData.platforms.indexOf(platform);
    if (index === -1) {
      formData.platforms = [...formData.platforms, platform];
    } else {
      formData.platforms = formData.platforms.filter(p => p !== platform);
    }
  }
</script>

<div 
  class="fixed inset-0 z-50 flex items-center justify-center backdrop-blur-sm"
  transition:fade={{ duration: 200 }}
>
  <!-- svelte-ignore a11y-click-events-have-key-events -->
  <!-- svelte-ignore a11y-no-static-element-interactions -->
  <div class="modal-overlay absolute inset-0 bg-slate-800 opacity-75" on:click={onClose}></div>
  
  <!-- svelte-ignore a11y-click-events-have-key-events -->
  <!-- svelte-ignore a11y-no-static-element-interactions -->
  <div 
    class="bg-white dark:bg-gray-900 p-4 rounded-lg shadow-lg w-full max-w-2xl relative z-60 overflow-y-auto max-h-[90vh]"
    on:click|stopPropagation
    transition:scale={{ duration: 300, start: 0.95 }}
  >
    <header class="flex justify-between items-start mb-4 text-gray-900 dark:text-gray-100">
      <h2 class="text-xl font-bold">
        {mode === 'create' ? 'Create Localization Entry' : 'Edit Localization Entry'}
      </h2>
      <button 
        class="text-2xl text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200" 
        on:click={onClose}
      >&times;</button>
    </header>

    <form on:submit|preventDefault={handleSubmit} class="space-y-4">
      <!-- First Row: Key, Type, and Rule ID (if Message type) -->
      <div class="flex gap-3">
        <div class="w-full">
          <label for="key" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Key</label>
          <input
            type="text"
            id="key"
            bind:value={formData.key}
            class="border p-1 w-full h-8 rounded dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
            placeholder="e.g., shared.cancel"
          />
        </div>
        <div class="w-40">
          <label for="type" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Type</label>
          <select
            id="type"
            bind:value={formData.type}
            on:change={handleTypeChange}
            class="border p-1 w-full h-8 rounded dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
          >
            {#each typeOptions as type}
              <option value={type}>{type}</option>
            {/each}
          </select>
        </div>
        {#if formData.type === 'Message'}
          <div class="w-1/10">
            <label for="ruleId" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Rule ID</label>
            <input
              type="number"
              id="ruleId"
              bind:value={formData.ruleId}
              class="border p-1 w-full h-8 rounded dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
              placeholder="e.g., 4"
            />
          </div>
        {/if}
      </div>

      <!-- Second Row: Content -->
      <div>
        <!-- svelte-ignore a11y-label-has-associated-control -->
        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Content</label>
        {#if formData.type === 'String'}
          <input
            type="text"
            bind:value={formData.defaultContent.value}
            class="border p-1 w-full h-8 rounded dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
            placeholder="Enter text content"
          />
        {:else if formData.type === 'Message'}
          <input
            type="text"
            bind:value={formData.defaultContent.title}
            class="border p-1 w-full h-8 rounded dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
            placeholder="Enter message title"
          />
        {/if}
        {#if errors.content}
          <p class="mt-1 text-sm text-red-600 dark:text-red-400">{errors.content}</p>
        {/if}
      </div>

      <!-- Third Row: Message Content (only for Message type) -->
      {#if formData.type === 'Message'}
        <div>
          <!-- svelte-ignore a11y-label-has-associated-control -->
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Message</label>
          <textarea
            bind:value={formData.defaultContent.message}
            class="border p-1 w-full rounded dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
            placeholder="Enter message content"
            rows="3"
          ></textarea>
        </div>
      {/if}

      <!-- Platforms -->
      <div>
        <!-- svelte-ignore a11y-label-has-associated-control -->
        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Platforms</label>
        <div class="flex flex-wrap gap-2">
          {#each platformOptions as platform}
            <button
              type="button"
              class="px-3 py-1 rounded-full text-sm {formData.platforms.includes(platform) 
                ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' 
                : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'}"
              on:click={() => togglePlatform(platform)}
            >
              {platform}
            </button>
          {/each}
        </div>
        {#if errors.platforms}
          <p class="mt-1 text-sm text-red-600 dark:text-red-400">{errors.platforms}</p>
        {/if}
      </div>

      <!-- Actions -->
      <div class="flex justify-end space-x-3 mt-6">
        <button
          type="button"
          on:click={onClose}
          class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600 dark:hover:bg-gray-600"
        >
          Cancel
        </button>
        <button
          type="submit"
          class="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:bg-blue-500 dark:hover:bg-blue-600"
        >
          {mode === 'create' ? 'Create' : 'Save'}
        </button>
      </div>
    </form>
  </div>
</div> 